import classNames from "classnames"
import { motion } from "motion/react"
import type { Transition } from "motion/react"

import { MAIN_LINK_CLASSES } from "."
import DashboardOptions from "./DashboardOptions"
import { useAuth } from "../../../auth/hooks"
import { CheckoutContext } from "../../../checkout/store"

import Link from "@components/elements/Link"
import ChevronIcon from "@components/elements/V2/Icons/ChevronIcon"
import Typography from "@components/elements/V2/Typography"
import { MainLink } from "@components/V2/Navigation/NavHeading"
import SubMenuBlock from "@components/V2/Navigation/SubMenuBlock"
import { Color } from "@constants/V2/color"
import { trackSegmentEvent } from "@utils/analytics"
import { withAppBaseUrl } from "@utils/url"
import React, { useState, useCallback, useContext } from "react"

const PANEL_TRANSITION: Transition = { duration: 0.3, ease: "easeInOut" }

interface MobileNavigationPanelsProps {
  mainLinks: MainLink[]
  mode: "light" | "dark"
  scrolled: boolean
  secondaryButtonText: string
  secondaryButtonLink: string | YocoCom.YocoLink
  secondaryButtonTrackingEvent?: string
  secondaryButtonTrackingEventKey?: string
  secondaryButtonTrackingEventValue?: string
  activeSubMenu: number | undefined
  handleMainLinkClick: (menuId: number) => void
  handleMainLinkClose: () => void
}

const MobileNavPanels: React.FC<MobileNavigationPanelsProps> = ({
  mainLinks,
  mode,
  scrolled,
  activeSubMenu,
  handleMainLinkClick,
  handleMainLinkClose,
}) => {
  const [activeSubSection, setActiveSubSection] = useState<number | undefined>()
  const [isDashboardMenuOpen, setIsDashboardMenuOpen] = useState(false)
  const { isAuthenticated, handleLogin } = useAuth()
  const { isHandlingLogin } = useContext(CheckoutContext)

  const oauthLoginEnabled = process.env.GATSBY_OAUTH_LOGIN === "true"

  const handleBackToMainMenu = useCallback(() => {
    setIsDashboardMenuOpen(false)
    setActiveSubSection(undefined)
    handleMainLinkClose()
  }, [handleMainLinkClose])

  const handleBackToSubMenu = useCallback(() => {
    setActiveSubSection(undefined)
  }, [])

  const handleSectionClick = useCallback(
    (sectionIndex: number, mainLinkIndex: number) => {
      const isExpanded = mainLinks[mainLinkIndex]?.expandSubmenus
      if (isExpanded) {
        setActiveSubSection(sectionIndex)
        handleMainLinkClick(mainLinkIndex)
      } else {
        setActiveSubSection(sectionIndex)
      }
    },
    [handleMainLinkClick, mainLinks]
  )

  const PANEL_BASE_CLASSES =
    "absolute overflow-y-scroll pb-144 pt-gutter-xs px-gutter-xs sm:px-gutter-sm md:px-gutter-md left-0 top-0 h-full w-full bg-white"

  let activePanel: 1 | 2 | 3 = 1

  if (activeSubSection !== undefined || isDashboardMenuOpen) {
    activePanel = 3
  } else if (
    activeSubMenu !== undefined &&
    !mainLinks[activeSubMenu]?.expandSubmenus
  ) {
    activePanel = 2
  }

  const getPanelX = (panel: number) =>
    panel === activePanel ? "0%" : panel < activePanel ? "-100%" : "100%"
  const panel1X = getPanelX(1)
  const panel2X = getPanelX(2)
  const panel3X = getPanelX(3)

  return (
    <div className="relative h-full w-full overflow-x-hidden">
      {/* ─ Panel #1: Main Links ── */}
      <motion.div
        className={PANEL_BASE_CLASSES}
        initial={false}
        animate={{ x: panel1X }}
        transition={PANEL_TRANSITION}
      >
        <div className="h-fit overflow-y-auto">
          {mainLinks.map((mainLink, index) => (
            <div
              className="sm:max-w-[330px]"
              key={`mobile-main-link-${mainLink.label}`}
            >
              <div className="w-full">
                {mainLink.link ? (
                  <Link
                    link={mainLink.link}
                    className="hover:bg-gray-50 mb-24 flex w-full items-center justify-between"
                  >
                    <Typography
                      color={Color.Charcoal}
                      font="grotesk"
                      weight="medium"
                      size="body-lg-fixed"
                      text={mainLink.label}
                    />
                    <ChevronIcon className="-rotate-90 transform transition-transform duration-200 ease-in-out" />
                  </Link>
                ) : mainLink.expandSubmenus ? (
                  <>
                    <Typography
                      color={Color.Charcoal}
                      font="grotesk"
                      weight="book"
                      size="subscript-sm-fixed"
                      className="mb-20 pt-[6px] uppercase"
                      text={mainLink.label}
                    />
                    {mainLink.subMenu?.content.map((section, sIdx) => (
                      <button
                        key={`mobile-section-${index}-${sIdx}`}
                        onClick={() => handleSectionClick(sIdx, index)}
                        className="hover:bg-gray-50 mb-24 flex w-full items-center justify-between"
                      >
                        <Typography
                          color={Color.Charcoal}
                          font="grotesk"
                          weight="medium"
                          size="body-lg-fixed"
                          text={section.label}
                        />
                        <ChevronIcon className="-rotate-90 transform transition-transform duration-200 ease-in-out" />
                      </button>
                    ))}
                  </>
                ) : (
                  <>
                    <Typography
                      color={Color.Charcoal}
                      font="grotesk"
                      weight="book"
                      size="subscript-sm-fixed"
                      className="mb-20 pt-[6px] uppercase"
                      text={mainLink.label}
                    />
                    <button
                      onClick={() => handleMainLinkClick(index)}
                      className="hover:bg-gray-50 mb-24 flex w-full items-center justify-between"
                    >
                      <Typography
                        color={Color.Charcoal}
                        font="grotesk"
                        weight="medium"
                        size="body-lg-fixed"
                        text={mainLink.label}
                      />
                      <ChevronIcon className="-rotate-90 transform transition-transform duration-200 ease-in-out" />
                    </button>
                  </>
                )}
              </div>
              {!mainLink.link && (
                <div className="mb-24 h-[1px] w-full bg-charcoal/10" />
              )}
            </div>
          ))}
        </div>
        {/* ─ Mobile + Tablet Bottom Buttons ── */}
        <div className="flex flex-col border-t border-charcoal/10 sm:border-none lg:hidden">
          {isAuthenticated ? (
            <button
              onClick={() => setIsDashboardMenuOpen(!isDashboardMenuOpen)}
              className="hover:bg-gray-50 mt-24 flex w-full items-center justify-between sm:max-w-[330px]"
            >
              <Typography
                color={Color.Charcoal}
                font="grotesk"
                weight="medium"
                size="body-md-fixed"
                text="My Dashboard"
              />
              <ChevronIcon className="-rotate-90 transform transition-transform duration-200 ease-in-out" />
            </button>
          ) : (
            <Link
              className={classNames(
                "!pb-0 transition-opacity duration-200 ease-in-out",
                MAIN_LINK_CLASSES,
                {
                  "text-black lg:text-white": mode === "dark" && !scrolled,
                },
                { "text-black": mode === "light" && scrolled }
              )}
              link={withAppBaseUrl("login")}
              onClick={(e) => {
                if (!oauthLoginEnabled) return

                e.preventDefault()
                if (isHandlingLogin) return

                trackSegmentEvent("nav_login", {
                  page: globalThis.location.pathname,
                })
                handleLogin()
              }}
            >
              <Typography
                text={"Login"}
                size="body-md-fixed"
                font="grotesk"
                weight="medium"
                color={Color.Charcoal}
              />
            </Link>
          )}

          <Link
            link="https://support.yoco.help/en/"
            className={classNames(
              "!pb-0 transition-opacity duration-200 ease-in-out",
              MAIN_LINK_CLASSES,
              {
                "text-black lg:text-white": mode === "dark" && !scrolled,
              },
              { "text-black": mode === "light" && scrolled }
            )}
          >
            <Typography
              text="Support"
              size="body-md-fixed"
              font="grotesk"
              weight="medium"
              color={Color.Charcoal}
            />
          </Link>
        </div>
      </motion.div>

      {/* ─ Panel #2: Submenu sections (for non-first main links) ── */}
      <motion.div
        className={classNames(PANEL_BASE_CLASSES, "flex sm:flex-col")}
        initial={false}
        animate={{ x: panel2X }}
        transition={PANEL_TRANSITION}
      >
        {/* Back Button (to Panel #1) */}
        <button className="flex gap-8" onClick={handleBackToMainMenu}>
          <ChevronIcon className="rotate-90" />
          <Typography
            color={Color.Charcoal}
            font="grotesk"
            weight="medium"
            size="body-md-fixed"
            text="Back"
            className="mb-32 hidden pt-[2px] sm:block"
          />
        </button>
        <div className="w-full pl-16 sm:pl-32">
          {activeSubMenu !== undefined &&
            !mainLinks[activeSubMenu]?.expandSubmenus && (
              <div className="w-full">
                <Typography
                  color={Color.Charcoal}
                  font="grotesk"
                  weight="book"
                  size="subscript-sm-fixed"
                  className="mb-20 pt-[6px] uppercase"
                  text={mainLinks[activeSubMenu].label}
                />
                {mainLinks[activeSubMenu].subMenu?.content.map(
                  (section, sIdx) => (
                    <button
                      key={`mobile-section-${section.label}`}
                      onClick={() => handleSectionClick(sIdx, activeSubMenu)}
                      className="hover:bg-gray-50 mb-24 flex w-full items-center justify-between sm:max-w-[330px]"
                    >
                      <Typography
                        color={Color.Charcoal}
                        font="grotesk"
                        weight="medium"
                        size="body-lg-fixed"
                        text={section.label}
                      />
                      <ChevronIcon className="-rotate-90 transform transition-transform duration-200 ease-in-out" />
                    </button>
                  )
                )}
              </div>
            )}
        </div>
      </motion.div>

      {/* ─ Panel #3: MenuContent (always third panel for first link, varies for others) ─────────── */}
      <motion.div
        className={classNames(PANEL_BASE_CLASSES, "flex sm:flex-col")}
        initial={false}
        animate={{ x: panel3X }}
        transition={PANEL_TRANSITION}
      >
        {/* Back Button */}
        <button
          className="flex gap-8"
          onClick={
            (activeSubMenu !== undefined &&
              mainLinks[activeSubMenu]?.expandSubmenus) ||
            isDashboardMenuOpen
              ? handleBackToMainMenu
              : handleBackToSubMenu
          }
        >
          <ChevronIcon className="rotate-90" />
          <Typography
            color={Color.Charcoal}
            font="grotesk"
            weight="medium"
            size="body-md-fixed"
            text="Back"
            className="mb-32 hidden pt-[2px] sm:block"
          />
        </button>

        <div className="pl-16 sm:pl-32">
          {/* Render that section's menuContent blocks */}
          {activeSubSection !== undefined && activeSubMenu !== undefined && (
            <div className="flex flex-col gap-32 overflow-y-auto px-4">
              {mainLinks[activeSubMenu].subMenu?.content[
                activeSubSection
              ].menuContent.map((contentBlock, cIdx) => (
                <SubMenuBlock
                  key={`mobile-sub-block-${cIdx}`}
                  {...contentBlock}
                />
              ))}
            </div>
          )}
        </div>

        {isDashboardMenuOpen && (
          <div className="flex w-full flex-col gap-32 overflow-y-auto overflow-x-clip px-4 pl-16 pt-[6px] sm:pl-32 sm:pt-0">
            <Typography
              color={Color.Charcoal}
              font="grotesk"
              weight="book"
              size="subscript-sm"
              className="uppercase tracking-wide"
              text="My Dashboard"
            />
            <DashboardOptions />
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default MobileNavPanels
